/**
 * LinkProviderDialog — after-the-fact directory linking for an UNLINKED
 * care-team row (directory care-web, founder feedback 2026-07-10: a
 * provider added from a case note "should be able to be linked to the
 * resource directory if possible").
 *
 * The same search-first flow the AddProviderDialog leads with (the ONE
 * DirectorySearch component), scoped to one decision: pick the Directory
 * profile this row IS, confirm, done. Linking attaches identity only —
 * display name and role stay the note's phrasing; specialty/organization
 * derive from the Directory on the next read. After a pick, "What linking
 * brings" lists the entry's phone, email and address, and says a phone or
 * address already typed for the client stays. PATCHes the providers link
 * route; success invalidates the ['clientRecord'] aggregate — one audited
 * re-read, never a cache patch.
 */

import { useSupportContact } from '@/hooks/useSupportContact';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { linkProviderRow, recordEditErrorMessage } from '@/lib/clientRecordEditApi';
import { cachedClientRecord } from '@/lib/clientRecordApi';
import { pronounForms } from '@/lib/pronounForms';
import { formatPhoneDisplay } from '@/lib/phone';
import { DirectorySearch } from '@/components/clientRecord/AddProviderDialog';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import type { SearchResult } from '@/lib/types';

type Picked =
  | { kind: 'individual'; id: string; name: string; row: SearchResult }
  | { kind: 'org'; id: string; name: string; row: SearchResult }
  | null;

export interface LinkProviderDialogProps {
  /** The unlinked care-team row being linked. */
  rowId: string;
  /** The row's display name — the search's framing ("who is this?"). */
  displayName: string;
  onClose: () => void;
}

export function LinkProviderDialog({
  rowId,
  displayName,
  onClose,
}: LinkProviderDialogProps): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<Picked>(null);
  const forms = pronounForms(cachedClientRecord(queryClient, tenantSlug, clientId)?.vitals.pronouns);
  const [error, setError] = useState<string | null>(null);
  // The error copy's contact, from the shell's cached tenant config.
  const contact = useSupportContact({ enabled: false });
  // A pick unmounts the search, so focus moves to the control that took
  // its place: Change.
  const unpickRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (picked !== null) unpickRef.current?.focus();
  }, [picked]);

  // The pick is read at the press and travels as the variables.
  const saveMut = useMutation<unknown, unknown, Picked>({
    mutationFn: (choice) => {
      if (choice === null) return Promise.reject(new Error('nothing picked'));
      return linkProviderRow(
        tenantSlug,
        clientId,
        rowId,
        choice.kind === 'org'
          ? { directory_org_id: choice.id }
          : { directory_individual_id: choice.id },
      );
    },
    onSuccess: async () => {
      // ONE audited re-read of the aggregate.
      await queryClient.invalidateQueries({ queryKey: ['clientRecord'] });
      onClose();
    },
    onError: (err) => setError(recordEditErrorMessage(err, null, contact)),
  });

  return (
    <Dialog
      title="Link to the Directory"
      size="md"
      testId="link-provider-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={picked === null || saveMut.isPending}
            loading={saveMut.isPending}
            data-testid="link-provider-save"
            onClick={() => saveMut.mutate(picked)}
          >
            Link profile
          </Button>
        </>
      }
    >
      <p className="text-xs text-ink-soft mb-3">
        <span className="font-semibold text-sage-800">{displayName}</span> joined{' '}
        {forms.possessive} care team by name. Find them in the Directory and{' '}
        {forms.possessive} record shows their phone, email and address from there.
      </p>

      {picked !== null ? (
        <div
          data-testid="link-provider-picked"
          className="mb-3 flex items-center gap-2 rounded-base border border-sage-200 bg-sage-50/60 px-2.5 py-1.5 text-sm"
        >
          <span className="rounded-pill bg-sage-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sage-400">
            {picked.kind === 'org' ? 'Org' : 'Person'}
          </span>
          <span className="font-semibold text-sage-800">{picked.name}</span>
          <Button
            ref={unpickRef}
            variant="ghost"
            size="sm"
            className="ml-auto"
            data-testid="link-provider-unpick"
            onClick={() => setPicked(null)}
          >
            Change
          </Button>
        </div>
      ) : (
        <DirectorySearch
          onPick={(row) => {
            setError(null);
            setPicked(
              row.type === 'organization'
                ? { kind: 'org', id: row.id, name: row.display_name, row }
                : { kind: 'individual', id: row.id, name: row.display_name, row },
            );
          }}
        />
      )}

      {picked !== null && <LinkingBrings row={picked.row} object={forms.object} />}

      {error !== null && (
        <p role="alert" data-testid="link-provider-error" className="text-xs text-pepper">
          {error}
        </p>
      )}
    </Dialog>
  );
}

/** What a link brings: the entry's phone, email and address, the ones it
 *  has, and that a value already typed for the client stays. */
function LinkingBrings({ row, object }: { row: SearchResult; object: string }): ReactNode {
  const facts: Array<[string, string]> = [];
  if (row.phone) facts.push(['Phone', formatPhoneDisplay(row.phone)]);
  if (row.email) facts.push(['Email', row.email]);
  if (row.address) facts.push(['Address', row.address]);
  return (
    <div data-testid="link-provider-brings" className="mb-3 rounded-base border border-sage-200 px-2.5 py-2 text-sm">
      <p className="text-xs font-semibold text-sage-800">What linking brings</p>
      {facts.length === 0 ? (
        <p className="text-xs text-ink-soft">Once the Directory has their phone, email or address, you'll see it here. If you have this information, suggest a change to their Directory listing.</p>
      ) : (
        <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
          {facts.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-xs text-ink-soft">{label}</dt>
              <dd className="text-ink">{value}</dd>
            </div>
          ))}
        </dl>
      )}
      <p className="mt-1 text-xs text-ink-soft">A phone or address already typed for {object} stays, and shows instead.</p>
    </div>
  );
}
